/* ============================================
   useSupabase Hook — Generic CRUD Operations
   Provides getAll, getById, add, update, remove, count
   for all tables defined in CrudTable type
   ============================================ */

import { supabase } from '@/lib/supabase'

/**
 * Valid table names for the cotizador schema.
 * Using string type directly to avoid Supabase's strict generic
 * table discrimination which prevents dynamic table access.
 */
type CrudTable = 'empresa' | 'servicios' | 'clientes' | 'cotizaciones' | 'perfiles_equipo' | 'facturas' | 'config_detraccion' | 'tipo_cambio' | 'proyectos' | 'tareas' | 'tarea_evidencias' | 'config_tarifa_cliente' | 'portal_accesos' | 'factura_comprobantes' | 'contactos' | 'contacto_empresa' | 'movimientos_caja' | 'pagos_programados' | 'pagos_recurrentes' | 'centrales_costo' | 'asignaciones_central' | 'costos_estimados' | 'gastos_proyecto' | 'notificacion_templates' | 'notificacion_log' | 'system_log' | 'conversaciones' | 'mensajes' | 'kb_articulos' | 'agente_config' | 'agente_kb_articulos' | 'social_strategy' | 'social_posts' | 'social_ai_conversations' | 'agente_memoria' | 'wiki_categorias' | 'wiki_articulos' | 'wiki_anotaciones' | 'wiki_comentarios' | 'wiki_versiones'

/**
 * Generic CRUD module that mirrors the old db.js interface.
 * All functions use the module-level supabase client singleton.
 *
 * IMPORTANT: Defined at module scope to guarantee a stable reference.
 * Returning a new object from the hook on every render caused infinite
 * re-render loops in components using [db] as a useCallback dependency.
 */

async function getAll<T>(table: CrudTable): Promise<T[]> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
  if (error) throw error
  return (data ?? []) as T[]
}

async function getById<T>(table: CrudTable, id: number): Promise<T | null> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('id', id)
    .single()
  if (error) return null
  return data as T
}

async function add(
  table: CrudTable,
  record: Record<string, unknown>
): Promise<number> {
  // Remove id before insert — let Supabase auto-generate it
  const cleaned = { ...record }
  delete cleaned.id

  const { data, error } = await supabase
    .from(table)
    .insert(cleaned as never)
    .select('id')
    .single()
  if (error) throw error
  return (data as unknown as { id: number }).id
}

async function update(
  table: CrudTable,
  record: Record<string, unknown>
): Promise<boolean> {
  const id = record.id as number
  const cleaned = { ...record }
  delete cleaned.id

  const { error } = await supabase
    .from(table)
    .update(cleaned as never)
    .eq('id' as never, id as never)
  if (error) throw error
  return true
}

async function remove(table: CrudTable, id: number): Promise<boolean> {
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('id' as never, id as never)
  if (error) throw error
  return true
}

async function count(table: CrudTable): Promise<number> {
  const { count: total, error } = await supabase
    .from(table)
    .select('*', { count: 'exact', head: true })
  if (error) throw error
  return total ?? 0
}

// Singleton — same object reference on every call, preventing infinite loops
const dbInstance = { getAll, getById, add, update, remove, count }

export function useSupabase() {
  return dbInstance
}
